import React, { useState, useMemo, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Copy, CheckCircle, MessageSquare, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

export interface TrialCreatedData {
  username: string;
  password: string;
  expDate?: string;
  duration?: number;
}

interface TrialCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  trial: TrialCreatedData;
}

const TrialCreatedModal: React.FC<TrialCreatedModalProps> = ({
  isOpen,
  onClose,
  trial
}) => {
  const [copiedField, setCopiedField] = useState<'username' | 'password' | 'message' | null>(null);

  const formattedMessage = useMemo(() => {
    const exp = trial.expDate || '—';
    const dur = trial.duration != null ? `${trial.duration} horas` : '—';
    return `✅ *Teste Gerado com Sucesso!*

👤 *Usuário:* ${trial.username}
🔑 *Senha:* ${trial.password}
📶 *Conexões:* 1
⏱️ *Duração:* ${dur}
📅 *Expira em:* ${exp}

Use estas credenciais para acessar o teste.`;
  }, [trial.username, trial.password, trial.expDate, trial.duration]);

  const copyToClipboard = async (text: string, field: 'username' | 'password' | 'message') => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        // Evita NotFoundError intermitente (o node pode já não estar no DOM)
        try {
          textarea.remove();
        } catch (_) {
          if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
        }
      }
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        // Evita NotFoundError intermitente (o node pode já não estar no DOM)
        try {
          textarea.remove();
        } catch (_) {
          if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
        }
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
      } catch {
        prompt('Copie o texto:', text);
      }
    }
  };

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  // Bloquear scroll do body enquanto o modal estiver aberto e restaurar ao fechar
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Fechar com tecla Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleClose]);

  // Garantir que trial tem dados mínimos antes de renderizar
  if (!isOpen) return null;
  if (!trial || !trial.username) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-created-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay: atrás do conteúdo para não capturar cliques */}
        <div className="fixed inset-0 bg-black/50 transition-opacity z-0" aria-hidden />

        {/* Conteúdo do modal: à frente do overlay e sem propagar clique para o backdrop */}
        <div
          className="relative z-10 inline-block w-full max-w-2xl px-6 pt-6 pb-6 my-8 overflow-hidden text-left align-middle bg-white shadow-xl rounded-lg border-2 border-green-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-2 rounded-full mr-3 bg-green-50">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <h3 id="trial-created-title" className="text-lg font-medium text-gray-900">
                Teste Gerado com Sucesso
              </h3>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Credenciais */}
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-green-700">Username</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={trial.username}
                    readOnly
                    className="font-mono text-sm bg-white"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(trial.username, 'username')}
                    className="flex-shrink-0"
                  >
                    {copiedField === 'username' ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-green-700">Senha</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={trial.password}
                    readOnly
                    type="text"
                    className="font-mono text-sm bg-white"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(trial.password, 'password')}
                    className="flex-shrink-0"
                  >
                    {copiedField === 'password' ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              {(trial.duration != null || trial.expDate) && (
                <div className="text-xs text-green-700 flex flex-wrap gap-4">
                  {trial.duration != null && (
                    <p><strong>Duração:</strong> {trial.duration} horas</p>
                  )}
                  {trial.expDate && (
                    <p><strong>Expira em:</strong> {trial.expDate}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mensagem para copiar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                <MessageSquare className="w-4 h-4 mr-2" />
                Mensagem para o Cliente
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(formattedMessage, 'message')}
                className="flex items-center"
              >
                {copiedField === 'message' ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Mensagem
                  </>
                )}
              </Button>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                {formattedMessage}
              </pre>
            </div>
          </div>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Copie e envie ao cliente</p>
                <p>Use o botão &quot;Copiar Mensagem&quot; e envie pelo WhatsApp ou outro canal.</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleClose}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default TrialCreatedModal;
